import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { isRunningInNativeApp, native } from '@lemoment/shared';

// Domains that should be treated as in-app routes when matched by a
// Universal Link / App Link. Anything else (a foreign https URL) is
// ignored so the deep link doesn't accidentally hijack navigation.
const APP_LINK_HOSTS = ['lemoment.kr'];

/**
 * Parse a deep link URL into an in-app path.
 *
 *   lemoment://post/123             → /post/123
 *   lemoment://post/123?from=push   → /post/123?from=push
 *   https://lemoment.kr/post/123    → /post/123
 *
 * Returns null if the URL doesn't map to an in-app route (e.g. an
 * https link to a different domain).
 */
export const toRoutePath = (rawUrl: string): string | null => {
  try {
    const parsed = new URL(rawUrl);

    if (parsed.protocol === 'lemoment:') {
      // URL spec parses `lemoment://post/123` as host=post, pathname=/123.
      // Combine them back into a single path segment.
      const host = parsed.host;
      const path = parsed.pathname || '';
      const combined = host ? `/${host}${path}` : path || '/';
      return combined + parsed.search + parsed.hash;
    }

    if (
      (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
      APP_LINK_HOSTS.includes(parsed.host)
    ) {
      return (parsed.pathname || '/') + parsed.search + parsed.hash;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Subscribe to deep links from the native shell. On every URL, the
 * matching in-app route is parsed and react-router navigates to it.
 * In a plain browser (no native shell) this hook is a no-op.
 */
export const useDeepLink = (): void => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isRunningInNativeApp()) {
      return;
    }

    return native.on('deepLink', ({ url }) => {
      const path = toRoutePath(url);
      if (!path) {
        console.warn('[deepLink] unsupported URL, ignoring:', url);
        return;
      }
      navigate(path);
    });
  }, [navigate]);
};
